// Audio Test — developer A/B page for the Udyog studio mic pipeline.
//
// Loaded at /audio-test. Does NOT require login (deliberately — you want to
// hand this URL to a beta tester on any device without granting them admin
// access). Everything runs client-side. No LiveKit connection is made here;
// this page directly instantiates the pipeline on getUserMedia, plays the
// processed audio to the headphones/speakers, and captures level metrics.
//
// Three test modes:
//   A. off        — Raw mic + browser AEC only (Chrome default WebRTC path)
//   B. dfn3-only  — Mic + Chrome AEC + DeepFilterNet3 only
//   C. dfn3+dsp   — Mic + Chrome AEC + DFN3 + broadcast DSP (production default)
//
// Record 6 seconds of audio for each mode, then A/B/C play back below.
// Live meters: input RMS, output RMS, output peak, clip indicator, latency.
// -----------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  StudioMicProcessor,
  MODES,
  DEFAULT_MODE,
  detectAudioMode,
  getStudioMicMode,
  setStudioMicMode,
} from "@/lib/studioMicProcessor";

const MODE_LABELS = {
  off: "Raw mic + AEC",
  "dfn3-only": "AEC + DeepFilterNet3",
  "dfn3+dsp": "AEC + DFN3 + Broadcast DSP",
};

const RECORD_MS = 6000;

// Compute RMS in dBFS from a 128-sample Float32 buffer.
function rmsDb(buf) {
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  const rms = Math.sqrt(s / buf.length) || 1e-8;
  return 20 * Math.log10(rms);
}

function peakDb(buf) {
  let p = 0;
  for (let i = 0; i < buf.length; i++) {
    const a = Math.abs(buf[i]);
    if (a > p) p = a;
  }
  return 20 * Math.log10(p || 1e-8);
}

function Meter({ label, dbValue, min = -60, max = 0, warn = -6, clip = -1 }) {
  const v = Math.max(min, Math.min(max, dbValue));
  const pct = ((v - min) / (max - min)) * 100;
  const isClip = dbValue > clip;
  const isWarn = !isClip && dbValue > warn;
  const color = isClip ? "bg-red-500" : isWarn ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div data-testid={`meter-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex justify-between text-xs text-neutral-400 mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{dbValue.toFixed(1)} dB</span>
      </div>
      <div className="h-2 rounded-full bg-neutral-900 overflow-hidden border border-neutral-800">
        <div className={`${color} h-full transition-[width] duration-75`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AudioTest() {
  const [mode, setMode] = useState(getStudioMicMode());
  const [status, setStatus] = useState("idle"); // idle | starting | running | error
  const [error, setError] = useState("");
  const [inRms, setInRms] = useState(-90);
  const [outRms, setOutRms] = useState(-90);
  const [outPeak, setOutPeak] = useState(-90);
  const [clipCount, setClipCount] = useState(0);
  const [initMs, setInitMs] = useState(0);
  const [supported, setSupported] = useState(true);
  const [recordingMode, setRecordingMode] = useState(null); // 'off'|'dfn3-only'|'dfn3+dsp'|null
  const [recordCountdown, setRecordCountdown] = useState(0);
  const [samples, setSamples] = useState({}); // { off: blobUrl, ... }

  // Refs for the audio graph we spin up.
  const rawStreamRef = useRef(null);
  const procRef = useRef(null);
  const meterCtxRef = useRef(null);
  const meterRafRef = useRef(null);
  const inAnalyserRef = useRef(null);
  const outAnalyserRef = useRef(null);
  const inBufRef = useRef(new Float32Array(1024));
  const outBufRef = useRef(new Float32Array(1024));
  const monitorAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);

  useEffect(() => {
    // Runtime capability check.
    const detected = detectAudioMode(mode);
    if (detected === "off" && mode !== "off") {
      setSupported(false);
    }
    return () => stopPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPipeline = async () => {
    try { cancelAnimationFrame(meterRafRef.current); } catch (_) {}
    try { procRef.current && await procRef.current.destroy(); } catch (_) {}
    try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { meterCtxRef.current && await meterCtxRef.current.close(); } catch (_) {}
    if (monitorAudioRef.current) {
      monitorAudioRef.current.srcObject = null;
      monitorAudioRef.current.pause();
    }
    procRef.current = null;
    rawStreamRef.current = null;
    meterCtxRef.current = null;
    inAnalyserRef.current = null;
    outAnalyserRef.current = null;
  };

  const startPipeline = async (nextMode) => {
    setError("");
    setStatus("starting");
    await stopPipeline();
    const started = performance.now();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 48000,
        },
        video: false,
      });
      rawStreamRef.current = stream;
      const rawTrack = stream.getAudioTracks()[0];

      // Meter AudioContext — separate from the processor's context on purpose,
      // so we always have an "in" reading even in mode='off'.
      const AC = window.AudioContext || window.webkitAudioContext;
      const mCtx = new AC({ sampleRate: 48000, latencyHint: "interactive" });
      meterCtxRef.current = mCtx;
      const mSrc = mCtx.createMediaStreamSource(stream);
      const inAn = mCtx.createAnalyser();
      inAn.fftSize = 2048;
      mSrc.connect(inAn);
      inAnalyserRef.current = inAn;

      let processedTrack;
      if (nextMode === "off") {
        processedTrack = rawTrack;
      } else {
        const proc = new StudioMicProcessor({ mode: nextMode });
        procRef.current = proc;
        await proc.init({ track: rawTrack });
        processedTrack = proc.processedTrack;
      }

      // Output analyser on the processed stream.
      const outStream = new MediaStream([processedTrack]);
      const outSrc = mCtx.createMediaStreamSource(outStream);
      const outAn = mCtx.createAnalyser();
      outAn.fftSize = 2048;
      outSrc.connect(outAn);
      outAnalyserRef.current = outAn;

      // Play processed audio back to headphones so the user can hear the
      // difference in real time. Uses an <audio> element rather than a Web
      // Audio destination so the browser routes it through the OS mixer with
      // normal echo protection.
      if (monitorAudioRef.current) {
        monitorAudioRef.current.srcObject = outStream;
        monitorAudioRef.current.muted = false;
        monitorAudioRef.current.play().catch(() => {});
      }

      setInitMs(Math.round(performance.now() - started));
      setStatus("running");
      setMode(nextMode);
      setStudioMicMode(nextMode);

      // Meter loop — 30 Hz is enough for a VU meter and cheap.
      const tick = () => {
        const inBuf = inBufRef.current;
        const outBuf = outBufRef.current;
        inAn.getFloatTimeDomainData(inBuf);
        outAn.getFloatTimeDomainData(outBuf);
        const iR = rmsDb(inBuf);
        const oR = rmsDb(outBuf);
        const oP = peakDb(outBuf);
        setInRms(iR);
        setOutRms(oR);
        setOutPeak(oP);
        if (oP > -1) setClipCount((c) => c + 1);
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setError(e?.message || String(e));
      setStatus("error");
      await stopPipeline();
    }
  };

  const stop = async () => {
    await stopPipeline();
    setStatus("idle");
    setInRms(-90);
    setOutRms(-90);
    setOutPeak(-90);
    setClipCount(0);
  };

  // Record a 6-second sample of the CURRENT processed stream.
  const startRecording = async (recMode) => {
    if (status !== "running" || recordingMode) return;
    // If the user asked to record a mode different from the running one,
    // hot-swap first.
    if (mode !== recMode) {
      await startPipeline(recMode);
      // Give DFN3 GRU state a moment to stabilize before we capture.
      await new Promise((r) => setTimeout(r, 400));
    }
    const monitor = monitorAudioRef.current;
    const outStream = monitor?.srcObject;
    if (!outStream) return;

    setRecordingMode(recMode);
    setRecordCountdown(Math.ceil(RECORD_MS / 1000));
    recordChunksRef.current = [];
    const rec = new MediaRecorder(outStream, { mimeType: "audio/webm" });
    mediaRecorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(recordChunksRef.current, { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      setSamples((s) => ({ ...s, [recMode]: url }));
      setRecordingMode(null);
      setRecordCountdown(0);
    };
    rec.start();

    // Countdown UI.
    let left = Math.ceil(RECORD_MS / 1000);
    const iv = setInterval(() => {
      left -= 1;
      setRecordCountdown(left);
      if (left <= 0) clearInterval(iv);
    }, 1000);

    setTimeout(() => {
      try { rec.state === "recording" && rec.stop(); } catch (_) {}
    }, RECORD_MS);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-8 sm:px-8">
      <audio ref={monitorAudioRef} className="hidden" />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Audio Pipeline Test</h1>
            <p className="text-sm text-neutral-400 mt-1">
              A/B compare the three modes of the Udyog studio mic pipeline.
            </p>
          </div>
          <Link
            to="/admin"
            className="text-sm text-neutral-400 hover:text-emerald-400"
            data-testid="audio-test-back-link"
          >
            ← Admin
          </Link>
        </div>

        {!supported && (
          <div className="mb-6 rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-3 text-amber-200 text-sm" data-testid="unsupported-banner">
            Your browser lacks WebAssembly SIMD or AudioWorklet support. Only the
            raw-mic mode will work here; DeepFilterNet3 is unavailable.
          </div>
        )}

        <Card className="bg-neutral-950 border border-neutral-800 p-5 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-xs uppercase tracking-wider text-neutral-500">Mode</span>
            {MODES.map((m) => (
              <button
                key={m}
                data-testid={`mode-btn-${m}`}
                onClick={() => startPipeline(m)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  mode === m && status === "running"
                    ? "bg-emerald-500 text-black border-emerald-400 font-semibold"
                    : "bg-neutral-900 text-neutral-300 border-neutral-800 hover:border-neutral-600"
                }`}
              >
                {MODE_LABELS[m]}
              </button>
            ))}
            <div className="flex-1" />
            <Button
              data-testid="stop-pipeline-btn"
              variant="ghost"
              onClick={stop}
              className="text-neutral-400 hover:text-white"
              disabled={status !== "running"}
            >
              Stop
            </Button>
          </div>

          {error && (
            <div className="mb-4 rounded border border-red-900 bg-red-950/40 px-3 py-2 text-red-300 text-xs" data-testid="pipeline-error">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Meter label="Input RMS" dbValue={inRms} />
            <Meter label="Output RMS" dbValue={outRms} />
            <Meter label="Output Peak" dbValue={outPeak} warn={-3} clip={-0.5} />
            <div className="flex items-center justify-between text-xs text-neutral-400" data-testid="stats-row">
              <div>
                <div className="uppercase tracking-wider text-neutral-500">Status</div>
                <div className="mt-1 text-neutral-200" data-testid="pipeline-status">{status}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-neutral-500">Init</div>
                <div className="mt-1 tabular-nums text-neutral-200" data-testid="init-ms">{initMs} ms</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-neutral-500">Clip events</div>
                <div className={`mt-1 tabular-nums ${clipCount ? "text-red-400" : "text-neutral-200"}`} data-testid="clip-count">{clipCount}</div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-neutral-950 border border-neutral-800 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">A/B Record & Compare</h2>
            {recordingMode && (
              <span className="text-sm text-emerald-400 tabular-nums" data-testid="recording-indicator">
                Recording {MODE_LABELS[recordingMode]} · {recordCountdown}s
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            Speak a normal sentence (e.g. "The quick brown fox jumps over the lazy dog.")
            after pressing Record. Do the same phrase in all three modes for a fair test.
          </p>
          <div className="space-y-3">
            {MODES.map((m) => (
              <div
                key={m}
                data-testid={`sample-row-${m}`}
                className="flex flex-wrap items-center gap-3 rounded border border-neutral-800 bg-neutral-900/40 px-3 py-2"
              >
                <div className="w-56 text-sm text-neutral-200">{MODE_LABELS[m]}</div>
                <Button
                  size="sm"
                  data-testid={`record-btn-${m}`}
                  onClick={() => startRecording(m)}
                  disabled={status !== "running" || !!recordingMode}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black"
                >
                  Record 6s
                </Button>
                {samples[m] ? (
                  <audio
                    controls
                    src={samples[m]}
                    className="flex-1 min-w-[240px] h-10"
                    data-testid={`playback-${m}`}
                  />
                ) : (
                  <span className="text-xs text-neutral-500">No sample yet.</span>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-neutral-950 border border-neutral-800 p-5 text-xs text-neutral-400 space-y-2">
          <p className="text-neutral-300 font-semibold">What each mode does</p>
          <p><span className="text-emerald-400">Raw mic + AEC:</span> Chrome's echo cancellation only. Baseline — every other WebRTC app defaults to this + browser NS.</p>
          <p><span className="text-emerald-400">AEC + DeepFilterNet3:</span> Above + AI denoise. No compression / no makeup gain. Most natural voice — best for premium mics.</p>
          <p><span className="text-emerald-400">AEC + DFN3 + Broadcast DSP:</span> Above + high-pass 80 Hz, gentle 3:1 compressor (threshold -18 dB, knee 6 dB), +3 dB makeup, brick-wall limiter. Production default.</p>
          <p className="pt-2">The mode you pick here is remembered and used automatically in every meeting on this device.</p>
        </Card>
      </div>
    </div>
  );
}
